import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { ShieldAlert } from 'lucide-react';

/*
 * Shown once per device before any Clinical Tools screen. The reader has to tick the
 * box; the acknowledgement is kept in localStorage under a version, so changing the
 * wording and bumping DISCLAIMER_VERSION shows it again.
 */

export const DISCLAIMER_VERSION = '3';

/** The wording, shared by the one-time sheet and the About box on the Clinical Tools page */
export const DISCLAIMER_TEXT =
  'These clinical tools and memory aids are provided for educational purposes to support, not replace, the judgment of the treating clinician. ' +
  'Content is maintained by the BCM Department of Anesthesiology and reflects general guidance that may not apply to every patient or situation. ' +
  'Before making a patient care decision, verify doses, protocols and recommendations against current institutional policies and primary sources. ' +
  'The Department and its affiliated hospitals assume no liability for clinical decisions made using this content.';
const KEY = 'clinical-disclaimer-ack';

export const hasAcceptedDisclaimer = () => {
  try { return localStorage.getItem(KEY) === DISCLAIMER_VERSION; } catch { return false; }
};

export const CLINICAL_PAGES = new Set([
  'clinical-tools', 'medication-reference', 'cv-medications', 'ob-medications', 'pediatric-medications',
  'regional-medications', 'preop-guide', 'asra-coags',
]);

const ClinicalDisclaimer = ({ onAccept, onDecline }) => {
  const [checked, setChecked] = useState(false);

  const accept = () => {
    try { localStorage.setItem(KEY, DISCLAIMER_VERSION); } catch { /* storage unavailable: show again next time */ }
    onAccept();
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 animate-fadeIn">
      <div role="dialog" aria-modal="true" aria-labelledby="disclaimer-title" className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl p-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] animate-slide-up">
        <div className="w-12 h-12 rounded-2xl bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-300 flex items-center justify-center mb-4">
          <ShieldAlert size={26} />
        </div>
        <h2 id="disclaimer-title" className="font-display text-xl font-bold text-gray-900 dark:text-white mb-3">Before you use the clinical tools</h2>
        <p className="text-[15px] leading-snug text-gray-700 dark:text-gray-300">{DISCLAIMER_TEXT}</p>
        <label className="mt-5 flex items-start gap-3 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => { setChecked(e.target.checked); }}
            className="mt-0.5 w-5 h-5 rounded border-gray-300 text-violet-700 focus:ring-violet-500"
          />
          <span className="text-[15px] text-gray-900 dark:text-white">I understand and accept this.</span>
        </label>
        <div className="mt-5 flex gap-3">
          <button onClick={() => { onDecline(); }} className="flex-1 py-3 rounded-xl font-semibold text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700">
            Not now
          </button>
          <button
            onClick={accept}
            disabled={!checked}
            className="flex-1 py-3 rounded-xl font-semibold text-white disabled:opacity-40"
            style={{ backgroundColor: '#6d28d9' }}
          >
            Continue
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClinicalDisclaimer;
